import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { getI18n } from 'react-i18next';

import { ITableCell, ITableRow } from '../../../components/app';
import { UsersSort } from '../../../components/app/organization/organization-users/components';
import { UserMenu } from '../../../components/app/organization/organization-users/components/user-menu';
import { ORGANIZATION_USERS_MAX_ROWS } from '../../../constants';
import { FetchStatus, Role, SortType, UsersSortBy } from '../../../enums';
import { ICreateOrganizationUser, IOrganizationUser } from '../../../interfaces';
import { getUserName, parseUserRoles, useDispatch, useSelector } from '../../../utils';
import { useAccount } from '../../account';
import {
  changeOrganizationUserRole,
  createOrganizationUser,
  deleteOrganizationUser,
  fetchOrganizationUsers,
  resetChangeOrganizationUserRoleStatus,
  resetCreateOrganizationUserStatus,
  resetDeleteOrganizationUserStatus,
  updateOrganizationUsersRoleFilter,
  updateOrganizationUsersSearchText,
  updateOrganizationUsersSort,
} from '../actions';
import { OrganizationUsersTableColumns } from '../constants';
import { OrganizationUsersTableColumn } from '../enums';
import { roleFilterInitialState } from '../reducers';
import {
  selectOrganizationUsers,
  selectOrganizationUsersRoleFilter,
  selectOrganizationUsersSearchText,
  selectOrganizationUsersSort,
} from '../selectors';
import { IOrganizationUsersRoleFilter, IOrganizationUsersSort } from '../types';
import { useOrganization, useOrganizationFilters } from './organization';

export const useOrganizationUsers = () => useSelector(selectOrganizationUsers);
export const useOrganizationUsersSearchText = () => useSelector(selectOrganizationUsersSearchText);
export const useOrganizationUsersRoleFilter = () => useSelector(selectOrganizationUsersRoleFilter);
export const useOrganizationUsersSort = () => useSelector(selectOrganizationUsersSort);

export function useResetCreateOrganizationUserStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetCreateOrganizationUserStatus());
  }, [dispatch]);
}

export function useResetChangeOrganizationUserRoleStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetChangeOrganizationUserRoleStatus());
  }, [dispatch]);
}

export function useResetDeleteOrganizationUserStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetDeleteOrganizationUserStatus());
  }, [dispatch]);
}

export function useUpdateOrganizationUsersSearchText() {
  const dispatch = useDispatch();

  return useCallback(
    (searchText?: string) => {
      dispatch(updateOrganizationUsersSearchText({ searchText: searchText ?? '' }));
    },
    [dispatch],
  );
}

export function useUpdateOrganizationUsersRoleFilter() {
  const dispatch = useDispatch();

  return useCallback(
    (filter?: IOrganizationUsersRoleFilter) => {
      dispatch(updateOrganizationUsersRoleFilter({ filters: filter ?? roleFilterInitialState }));
    },
    [dispatch],
  );
}

export function useUpdateOrganizationUsersSort() {
  const dispatch = useDispatch();

  return useCallback(
    (sort: IOrganizationUsersSort) => {
      dispatch(updateOrganizationUsersSort({ sort }));
    },
    [dispatch],
  );
}

export function useFetchOrganizationUsers() {
  const dispatch = useDispatch();
  const organization = useOrganization();

  useEffect(() => {
    if (organization.status.fetchUsers === FetchStatus.IDLE) {
      dispatch(fetchOrganizationUsers());
    }
  }, []);
}

export function useCreateOrganizationUser() {
  const dispatch = useDispatch();

  return useCallback(
    (user: ICreateOrganizationUser) => {
      dispatch(createOrganizationUser({ user }));
    },
    [dispatch],
  );
}

export function useChangeOrganizationUserRole() {
  const dispatch = useDispatch();

  return useCallback(
    (user: IOrganizationUser) => {
      dispatch(changeOrganizationUserRole({ user }));
    },
    [dispatch],
  );
}

export function useDeleteOrganizationUser() {
  const dispatch = useDispatch();

  return useCallback(
    (user: IOrganizationUser) => {
      dispatch(deleteOrganizationUser({ user }));
    },
    [dispatch],
  );
}

export function useOrganizationUsersTableRows(maxRows: boolean): ITableRow[] {
  const users = useFilteredOrganizationUsers(maxRows);

  return useMemo(() => {
    return users.map((user: IOrganizationUser) => ({
      id: user.username,
      elements: OrganizationUsersTableColumns.map(({ column, minWidth }) => {
        switch (column) {
          case OrganizationUsersTableColumn.NAME:
            return {
              element: createElement('span', {}, getUserName(user)),
              minWidth: minWidth(!maxRows),
            };
          case OrganizationUsersTableColumn.EMAIL:
            return {
              element: createElement('span', {}, user.username),
              minWidth: minWidth(!maxRows),
            };
          case OrganizationUsersTableColumn.ROLE:
            return {
              element: createElement('span', {}, parseUserRoles(user)),
              minWidth: minWidth(!maxRows),
            };
          case OrganizationUsersTableColumn.MENU:
            return {
              element: maxRows ? createElement('span') : createElement(UserMenu, { user }),
              minWidth: minWidth(!maxRows),
            };
        }
      }),
    }));
  }, [users]);
}

export function useOrganizationUsersTableHeaders(sortable: boolean): ITableCell[] {
  return OrganizationUsersTableColumns.map(({ column, minWidth }) => {
    const i18n = getI18n();
    switch (column) {
      case OrganizationUsersTableColumn.NAME:
        return {
          element: sortable
            ? createElement(UsersSort, { by: UsersSortBy.NAME })
            : createElement('span', {}, i18n.t('organization.users.table.name')),
          minWidth: minWidth(sortable),
        };
      case OrganizationUsersTableColumn.EMAIL:
        return {
          element: sortable
            ? createElement(UsersSort, { by: UsersSortBy.EMAIL })
            : createElement('span', {}, i18n.t('organization.users.table.email')),
          minWidth: minWidth(sortable),
        };
      case OrganizationUsersTableColumn.ROLE:
        return {
          element: createElement('span', {}, i18n.t('organization.users.table.role')),
          minWidth: minWidth(sortable),
        };
      default:
        return {
          element: createElement('span'),
          minWidth: minWidth(sortable),
        };
    }
  });
}

export function useFilteredOrganizationUsers(maxRows: boolean): IOrganizationUser[] {
  const { account } = useAccount();
  const users = useOrganizationUsers().filter((it) => it.username !== account?.username);
  if (maxRows) {
    return users.slice(0, ORGANIZATION_USERS_MAX_ROWS);
  }

  const searchText = useOrganizationUsersSearchText().toLowerCase();
  const sort = useOrganizationUsersSort();
  const filters = useOrganizationFilters();

  const usersSorted = useMemo(() => {
    if (sort.type === SortType.IDLE) {
      return users;
    }

    return [...users].sort((a: IOrganizationUser, b: IOrganizationUser) => {
      if (sort.type === SortType.ASC) {
        switch (sort.by) {
          case UsersSortBy.NAME:
            return getUserName(a).localeCompare(getUserName(b));
          case UsersSortBy.EMAIL:
            return a.username.localeCompare(b.username);
          default:
            return 0;
        }
      } else {
        switch (sort.by) {
          case UsersSortBy.NAME:
            return getUserName(b).localeCompare(getUserName(a));
          case UsersSortBy.EMAIL:
            return b.username.localeCompare(a.username);
          default:
            return 0;
        }
      }
    });
  }, [users, sort]);

  const usersFilteredBySearch: IOrganizationUser[] = useMemo(() => {
    return usersSorted.filter((user: IOrganizationUser) => {
      const userDataToSearch = {
        name: getUserName(user).toLowerCase(),
        email: user.username.toLowerCase(),
      };
      const values: string[] = Object.values(userDataToSearch);
      return values.some((value: string) => value.includes(searchText));
    });
  }, [usersSorted, searchText]);

  const usersFilteredByFilters: IOrganizationUser[] = useMemo(() => {
    const activeRoleFilters: Role[] = (Object.keys(filters.role) as Role[]).filter((key) => filters.role[key]);

    return usersFilteredBySearch.filter(
      (user: IOrganizationUser) =>
        !activeRoleFilters.length || user.roles.some((role) => activeRoleFilters.includes(role)),
    );
  }, [usersFilteredBySearch, filters]);

  return usersFilteredByFilters;
}

export function useAnyOrganizationUsersFiltersActive() {
  const organization = useOrganization();
  const [isAnyActive, setIsAnyActive] = useState(false);

  useEffect(() => {
    const isAnyFilterActive = Object.values({ ...organization.filters.role }).some((filter) => !!filter);

    if (organization.searchText.users !== '' || isAnyFilterActive) {
      setIsAnyActive(true);
    } else {
      setIsAnyActive(false);
    }
  }, [organization.searchText.users]);

  return isAnyActive;
}
